import isStaffUserId from '@medflex/utils/isStaffUserId';
import { USER_TYPES } from '@medflex/utils/constants';
import makeApiRequest from '../../../api/makeApiRequest';
import getFullNameWithTitle from '../../../utils/getFullNameWithTitle';

const mapStaff = ({ id, attributes }) => ({
  id,
  ...attributes,
  userId: id,
  initials: `${attributes.firstName[0]}${attributes.lastName[0]}`,
  fullNameWithTitle: attributes.workplace || getFullNameWithTitle.get(attributes),
});

const Staff = {
  name: 'staff',
  state: {
    staff: [],
  },
  reducers: {
    onLoaded(state, staff) {
      return { ...state, staff };
    },
    onUpdated(state, payload) {
      const staffMembers = state.staff.map(s => (s.id === payload.id ? { ...payload } : s));
      return { ...state, staff: staffMembers };
    },
    onDeleted(state, id) {
      return {
        ...state,
        staff: state.staff.filter(s => s.id !== id),
      };
    },
    onCreated(state, payload) {
      return { ...state, staff: [...state.staff, { ...payload }] };
    },
    onModified(state, { id, attributes }) {
      const isExisting = state.staff.some(s => s.id === id);
      if (isExisting) {
        return {
          ...state,
          staff: [...state.staff.map(s => (s.id === id ? mapStaff({ id, attributes }) : s))],
        };
      }
      return { ...state, staff: [...state.staff, mapStaff({ id, attributes })] };
    },
  },
  messageListeners: {
    USER_DELETED({ payload }) {
      const { id } = payload;
      if (id && isStaffUserId(id)) this.onDeleted(id);
    },
    USER_MODIFIED({ payload }) {
      const { id } = payload;
      if (id && isStaffUserId(id)) this.onModified(payload);
    },
  },
  effects: dispatch => ({
    async load(_, { oidcSession, superuser }) {
      const physicianUserId = superuser.flags.isPhysician
        ? superuser.id
        : superuser.attributes.physicianUserId;
      const url = `/physicians/${physicianUserId}/staff`;
      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession);
      const { error, data } = await request;
      if (!error) {
        this.onLoaded(data.data.map(member => mapStaff(member)));
      }
    },
    async create({ payload }, { oidcSession, superuser }) {
      dispatch.errors.remove({ realm: 'staff', type: 'create' });
      const body = {
        data: {
          type: USER_TYPES.STAFF,
          attributes: { ...payload },
        },
      };
      const [request] = makeApiRequest(
        { url: `/physicians/${superuser.id}/staff`, method: 'post', data: body },
        oidcSession,
      );
      const { error, data } = await request;
      if (!error) {
        this.onCreated(mapStaff(data.data));
      } else {
        dispatch.errors.add({ realm: 'staff', type: 'create', error });
        throw new Error(error);
      }
    },
    async update({ payload }, { oidcSession }) {
      const url = `/users/${payload.id}`;
      const { id, ...attributes } = payload;
      const body = {
        data: {
          type: USER_TYPES.STAFF,
          id,
          attributes: {
            ...attributes,
          },
        },
      };
      const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
      const { error, data } = await request;
      if (!error) {
        this.onUpdated(mapStaff(data.data));
      } else throw error;
    },
    async delete({ id }, { oidcSession }) {
      const url = `/users/${id}`;
      const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
      const { error } = await request;
      if (!error) {
        this.onDeleted(id);
      } else throw error;
    },
  }),
};

export default Staff;
